import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';
import { AnalyticsPayload, VendorAnalyticsCompletedState } from '../../interfaces/interface';
import extraReducers from '../../extraReducers/vendorAnalytics';

const initialState: VendorAnalyticsCompletedState = {
  VendorAnalyticsCompleted: {
    count: undefined,
    data: []
  },
  VendorAnalytics: {
    count: undefined,
    data: []
  },
  error: null,
  getVendorAnalyticsStatus: STATUS.NOT_STARTED,
  getVendorAnalyticsMessage: "",
  getVendorStatus: STATUS.NOT_STARTED,
  getVendorMessage: ""
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toPercentage = (numerator: number, denominator: number): number => {
  if (denominator <= 0) {
    return 0;
  }
  return Number(((numerator / denominator) * 100).toFixed(2));
};

const normalizeVendorRow = (row: Record<string, any>) => {
  const acceptedAsIs = toNumber(
    row?.quality_accepted_as_is_tasks ??
    row?.total_quality_accepted_as_is ??
    row?.accepted_as_is_count ??
    0
  );
  const acceptedWithEdits = toNumber(
    row?.quality_accepted_with_edits_tasks ??
    row?.total_quality_accepted_with_edits ??
    row?.accepted_with_edits_count ??
    0
  );
  const rejected = toNumber(
    row?.quality_historical_rejected_tasks ??
    row?.quality_rejected_tasks ??
    row?.total_quality_rejected ??
    row?.rejected_count ??
    0
  );
  const reviewedTotal = toNumber(row?.total_reviewed ?? acceptedAsIs + acceptedWithEdits + rejected);

  const acceptedAsIsPercentage = toNumber(
    row?.quality_accepted_as_is_percentage ??
    row?.accepted_as_is_percentage ??
    toPercentage(acceptedAsIs, reviewedTotal)
  );
  const acceptedWithEditsPercentage = toNumber(
    row?.quality_accepted_with_edits_percentage ??
    row?.accepted_with_edits_percentage ??
    toPercentage(acceptedWithEdits, reviewedTotal)
  );
  const rejectedPercentage = toNumber(
    row?.quality_rejected_percentage ??
    row?.rejected_percentage ??
    toPercentage(rejected, reviewedTotal)
  );

  return {
    ...row,
    quality_accepted_as_is_tasks: acceptedAsIs,
    quality_accepted_with_edits_tasks: acceptedWithEdits,
    quality_historical_rejected_tasks: rejected,
    quality_rejected_tasks: rejected,
    quality_accepted_as_is_percentage: acceptedAsIsPercentage,
    quality_accepted_with_edits_percentage: acceptedWithEditsPercentage,
    quality_rejected_percentage: rejectedPercentage,
    total_quality_accepted: acceptedAsIs + acceptedWithEdits,
  };
};

export const getCompletedVendorAnalytics = createAsyncThunk('project/qa-analytics/overview/vendor/get',
  async (payload: AnalyticsPayload = {}, thunkAPI) => {
    try {
      const { search, page, pageSize, sort_by, sort_order, project_id } = payload;
      const queryString = new URLSearchParams({
        ...(search && { search }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { page_size: pageSize.toString() }),
        ...(sort_by && { sort_by }),
        ...(sort_order && { sort_order }),
        ...(project_id && { project_id }),
        tab: 'vendors',
      }).toString();
      const response = await api.get(`project/company/vendor-analytics/vendors?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getVendorAnalytic = createAsyncThunk('/project/company/vendor/stage-wise-analytics/v2/get',
  async (payload: AnalyticsPayload = {}, thunkAPI) => {
    try {
      const { vendor_id, project_id } = payload;
      const queryString = new URLSearchParams({
        ...(vendor_id && { vendor_id }),
        ...(project_id && { project_id }),
      }).toString();
      const response = await api.get(`/project/company/vendor/stage-wise-analytics/v2/?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return {
          ...data,
          data: Array.isArray(data?.data)
            ? data.data.map((row: Record<string, any>) => normalizeVendorRow(row))
            : [],
        };
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

const vendoranalyticsCompletedSlice = createSlice({
  name: 'vendorAnalyticsCompleted',
  initialState,
  reducers: {
  },
  extraReducers
});

export default vendoranalyticsCompletedSlice.reducer;
